import { useState, type ReactNode } from 'react';
import { RecipientSource, WorkOrderCheckoutLineKind, assertNever, type WorkOrderDto } from '@signalvote/shared';
import { Button } from '@/components/ui';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import {
  Confidence,
  VoterEstimate,
  formatCents,
  formatUnitCents,
  times,
  useBuyList,
  type BuyList,
  type Estimate,
} from '@/components/work-orders';
import { useWorkOrder, useWorkOrderCheckoutPreview } from '@/hooks/queries';
import { ExpressStep, type ExpressStepProps } from './ExpressStep';
import { listTotal, splitDollars, type ListTotal } from './listPrice';

interface BuyListStepProps {
  workOrder: WorkOrderDto;
  /** The step's frame: its key, Back and the preview beside it. The order bar's total is the price card's. */
  frame: Pick<ExpressStepProps, 'step' | 'onBack' | 'aside'>;
  /** The Who gets it choices, with the list builder in the Buy a list slot. */
  picker: (buy: ReactNode) => ReactNode;
  onNext?: () => void;
}

/**
 * Who gets it, buying a list: the voters to text read as a sentence, always a phones list, priced live
 * as it is built. Add this list quotes it as a Voter data Draft and pairs the send with that Draft, so
 * both are paid together and the order bar carries both lines; Remove takes it back out.
 */
export function BuyListStep(props: BuyListStepProps) {
  const { pairedVoterDataWorkOrderId, recipientSource } = props.workOrder.details;
  const pairedId = recipientSource === RecipientSource.BoughtList ? (pairedVoterDataWorkOrderId ?? undefined) : undefined;
  const paired = useWorkOrder(pairedId);
  // The builder restores the paired list once, when it mounts, so it waits for the quote.
  if (paired.isLoading) return <ExpressStep {...props.frame}>{props.picker(null)}</ExpressStep>;
  return <BuyListBuilder {...props} pairedOrder={paired.data ?? null} />;
}

function BuyListBuilder({ workOrder, frame, picker, onNext, pairedOrder }: BuyListStepProps & { pairedOrder: WorkOrderDto | null }) {
  const buy = useBuyList(workOrder, pairedOrder);
  const price = useListPrice(buy, workOrder);
  const priced = !buy.choosingArea && price;
  const unpriced = priced && priced.total.kind === 'failed' ? 'We couldn’t work out the total. Try again above.' : undefined;

  return (
    <ExpressStep
      {...frame}
      summary={priced && <BarTotal total={priced.total} />}
      primary={
        buy.isPaired
          ? { label: 'Continue', disabled: unpriced !== undefined, heldBecause: unpriced, onPress: () => onNext?.() }
          : {
              label: 'Add this list',
              disabled: !buy.canPair,
              heldBecause: buy.unanswered,
              busy: buy.pairing,
              busyLabel: 'Adding…',
              onPress: buy.pair,
            }
      }
    >
      {picker(
        <div className="space-y-4">
          {buy.sentence}
          {priced && <PriceCard price={priced} buy={buy} />}
          {!buy.choosingArea && <ListPreviewButton buy={buy} />}
        </div>,
      )}
    </ExpressStep>
  );
}

const exact = (value: number): Estimate => ({ confidence: Confidence.Exact, value });

/** The list's price as the card and the order bar show it, line by line, with the one total both share. */
interface ListPrice {
  /** Voters on the list: the live count while it is built, the server's quote once it is added. */
  records: Estimate;
  recordUnitCents: number;
  list: Estimate;
  /** The per-text price, or null until the server has priced the send. An image doesn't change it. */
  textUnitCents: number | null;
  texts: Estimate | null;
  withImage: boolean;
  /** The one-time registration a first send owes: 0 when none is owed, null until the server says. */
  registrationCents: number | null;
  total: ListTotal;
  retry: () => void;
}

/**
 * Prices the list on screen: the list from its live count, or from the server's quote once added; the
 * texts at the server's per-text price, one to every voter on it, as checkout bills them; and the
 * registration line the server's preview carries. Null while there is no list to price.
 */
function useListPrice(buy: BuyList, workOrder: WorkOrderDto): ListPrice | null {
  const preview = useWorkOrderCheckoutPreview(workOrder.id);
  const shown =
    buy.isPaired && buy.quote
      ? { records: exact(buy.quote.records), list: exact(buy.quote.priceCents) }
      : buy.estimate && { records: buy.estimate.records, list: buy.estimate.priceCents };
  if (!shown) return null;
  const lines = preview.isError ? undefined : preview.data?.lines;
  const textUnitCents = lines?.find((line) => line.kind === WorkOrderCheckoutLineKind.TextSend)?.unitAmountCents ?? null;
  const registrationCents = lines ? (lines.find((line) => line.kind === WorkOrderCheckoutLineKind.Registration)?.amountCents ?? 0) : null;
  const texts = textUnitCents === null ? null : times(shown.records, textUnitCents);
  return {
    ...shown,
    recordUnitCents: buy.pricePerRecordCents,
    textUnitCents,
    texts,
    withImage: workOrder.details.imagePreviewUrl != null,
    registrationCents,
    total: listTotal({ list: shown.list, texts, registrationCents, failed: preview.isError }),
    retry: () => void preview.refetch(),
  };
}

function PriceRow({ label, detail, amount }: { label: string; detail: ReactNode; amount: ReactNode }) {
  return (
    <li className="flex items-start justify-between gap-4">
      <div>
        <p className="text-base text-foreground">{label}</p>
        {detail && <p className="text-sm text-muted-foreground tabular-nums">{detail}</p>}
      </div>
      <p className="text-base font-semibold tabular-nums text-foreground">{amount}</p>
    </li>
  );
}

/** A total with its cents set smaller, the way the card and the bar both show it. */
function Dollars({ cents, size, testId }: { cents: number; size: 'card' | 'bar'; testId: string }) {
  const split = splitDollars(cents);
  return (
    <p data-testid={testId} className="font-bold tabular-nums leading-none text-foreground">
      <span className={size === 'card' ? 'text-4xl' : 'text-2xl sm:text-3xl'}>{split.dollars}</span>
      <span className={size === 'card' ? 'text-xl' : 'text-base sm:text-lg'}>{split.cents}</span>
    </p>
  );
}

const COUNTING = <p className="text-sm text-muted-foreground">Counting…</p>;

/**
 * What the person will pay for the list and the texts to it, line by line, with the total the order bar
 * repeats. The total waits for every line, and a price that can't be had shows no total at all.
 */
function PriceCard({ price, buy }: { price: ListPrice; buy: BuyList }) {
  const { total } = price;
  return (
    <div className="space-y-3">
      <section aria-labelledby="list-price-title" className="rounded-2xl border border-border p-5">
        <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
          <h3 id="list-price-title" className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            What you’ll pay
          </h3>
          <p className="text-xs text-muted-foreground sm:text-sm">Updates as you change the list</p>
        </div>
        <ul className="mt-5 space-y-5">
          <PriceRow
            label="Voter list"
            detail={
              <>
                <VoterEstimate estimate={price.records} /> voters × {formatUnitCents(price.recordUnitCents)}
              </>
            }
            amount={<VoterEstimate estimate={price.list} dollars />}
          />
          <PriceRow
            label="Texts"
            detail={
              price.textUnitCents !== null && (
                <>
                  <VoterEstimate estimate={price.records} /> × {formatUnitCents(price.textUnitCents)}
                  {price.withImage && ' · image included'}
                </>
              )
            }
            amount={price.texts ? <VoterEstimate estimate={price.texts} dollars /> : '–'}
          />
          {!!price.registrationCents && (
            <PriceRow label="Carrier registration" detail="One time, before your first text" amount={formatCents(price.registrationCents)} />
          )}
        </ul>
        <div className="mt-5 flex items-end justify-between gap-4 border-t border-border pt-5">
          <p className="text-base text-foreground">Total</p>
          {total.kind === 'priced' && <Dollars cents={total.cents} size="card" testId="list-total" />}
          {total.kind === 'counting' && COUNTING}
          {total.kind === 'failed' && (
            <p role="alert" className="text-right text-sm text-destructive">
              Couldn’t work out the total.{' '}
              <button type="button" onClick={price.retry} className="font-semibold underline underline-offset-2">
                Try again
              </button>
            </p>
          )}
        </div>
        {buy.quote && !buy.isPaired && <p className="mt-4 text-sm text-muted-foreground">Adding it replaces the list in your order.</p>}
        {buy.tooSmall && (
          <p role="status" className="mt-4 text-sm text-muted-foreground">
            {buy.tooSmall}
          </p>
        )}
        <p className="mt-4 text-sm text-muted-foreground">
          Nothing is charged until you pay on the last step. List and texts are priced on every voter, including any whose number
          can’t be reached.
        </p>
      </section>
      {buy.isPaired && (
        <Button type="button" variant="outline" size="sm" disabled={buy.removing} onClick={buy.remove}>
          {buy.removing ? 'Removing…' : 'Remove from order'}
        </Button>
      )}
    </div>
  );
}

/** The order bar's figure: the card's total, or no figure while it can't be had. */
function BarTotal({ total }: { total: ListTotal }) {
  switch (total.kind) {
    case 'priced':
      return (
        <div className="min-w-0 text-right">
          <Dollars cents={total.cents} size="bar" testId="order-total" />
          <p className="mt-1 truncate text-xs text-muted-foreground">
            Total · {total.withRegistration ? 'list, texts and registration' : 'list and texts'}
          </p>
        </div>
      );
    case 'counting':
      return COUNTING;
    case 'failed':
      return null;
    default:
      return assertNever(total);
  }
}

/** Preview on the list step shows who is on the list, never the message. */
function ListPreviewButton({ buy }: { buy: BuyList }) {
  const [open, setOpen] = useState(false);
  const { estimate } = buy;
  if (!estimate) return null;
  return (
    <div>
      <Button type="button" variant="outline" className="w-full" onClick={() => setOpen(true)}>
        Preview
      </Button>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="bottom" className="rounded-t-2xl pb-10">
          <SheetHeader>
            <SheetTitle>Who’s on this list</SheetTitle>
          </SheetHeader>
          <div className="mt-4 space-y-3 text-sm">
            <p className="text-base font-medium leading-snug text-foreground">{buy.sentenceText}</p>
            <p className="tabular-nums">
              <b className="font-semibold text-foreground">
                <VoterEstimate estimate={estimate.voters} />
              </b>{' '}
              voters with a cell phone, in <VoterEstimate estimate={estimate.households} /> households.
            </p>
            {buy.columns.length > 0 && <p className="text-muted-foreground">Each voter comes with: {buy.columns.join(', ')}.</p>}
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
